import { useMemo, useEffect, useState, useContext } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Button, Chip, CircularProgress, ClickAwayListener, Paper, Popper, TextField, Typography } from '@mui/material';
import { Box, Grid } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { StaticTimePicker } from '../components/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import _ from 'lodash';

import { CREATE, DataAPIContext, GET_LIST } from '../api';
import { useIsWidthUp } from '../utils';
import { convertToAmPm, isUSLocale } from '../utils/date';
import { ContextStore } from '../store/contextStore';
import { tripHasDepartureCategory } from '../utils/tagCategories';

const useStyles = makeStyles((theme) => ({
    optionsMenu: {
        display: 'flex',
        maxHeight: '300px',
        maxWidth: '100%',
        width: '100%',
        overflowY: 'auto',
        flexDirection: 'column',
        backgroundColor: 'white',
        position: 'absolute',
        top: '3.15rem',
        borderRadius: '5px',
        left: 0,
        right: 0,
        zIndex: 1000,
        boxShadow: '0px 8px 10px rgba(0, 0, 0, 0.14), 0px 3px 14px rgba(0, 0, 0, 0.12), 0px 5px 5px rgba(0, 0, 0, 0.2)',
    },
    optionsGroupHeading: {
        marginLeft: '1rem',
        marginTop: '1rem',
        color: grey[600],
        fontSize: '80%',
    },
    optionsWrapper: {
        maxHeight: '270px',
        overflow: 'auto',
    },
    optionItem: {
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        borderBottom: 'solid 1px #D8D8D8',
        zIndex: 10000,
        '&:hover': {
            backgroundColor: '#efefef',
        },
    },
    addDepartureButton: {},
}));

const TripDepartures = ({ trip, disabled, onChange, clientErrors, setClientErrors }) => {
    const classes = useStyles();
    const dataAPI = useContext(DataAPIContext);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState(null);
    const [firstRender, setFirstRender] = useState(true);
    const [clearInput, setClearInput] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');
    const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
    const [isFirstRender, setIsFirstRender] = useState(true);

    const [newDepartureTag, setNewDepartureTag] = useState({
        category: "departure",
        data: {},
        name: '',
    });

    const { tourOperator } = useContext(ContextStore);
    const hasDepartureTagCategory = useMemo(() => tripHasDepartureCategory(trip, tourOperator), [trip, tourOperator]);
    const sortedDepartures = useMemo(() => _.sortBy(trip.departures || [], [(o) => o.tag.name]), [trip.departures]);
    const hasTripDepartures = () => trip.departures.length > 0;
    
    useEffect(() => {
        setClientErrors((prev) => ({ ...prev, isMissingDeparture: !isFirstRender && hasDepartureTagCategory && !hasTripDepartures() }));
        setIsFirstRender(false);
    }, [hasDepartureTagCategory, trip.departures]);

    const onAddDeparture = (nt) => {
        if (!nt) {
            return;
        }
        const nd = { group_tour: trip.id, tag: nt };
        onChange((prevState) => {
            const existing_departures = prevState.departures || [];
            const is_new = (prevState.departures || []).filter((x) => x.tag.id === nt.id).length <= 0;
            if (!is_new) {
                return prevState;
            }
            return {
                ...prevState,
                departures: _.sortBy(existing_departures.concat(nd), [(o) => o.tag.name]),
            };
        });
    };

    const onDeleteDeparture = (ev, idx) => {
        onChange((prevState) => {
            return {
                ...prevState,
                departures: _.sortBy(prevState.departures || [], [(o) => o.tag.name]).filter((x, i) => i !== idx),
            };
        });
    };

    useEffect(() => {
        if (clearInput) {
            setClearInput(false);
            handleTextOnBlur();
            return;
        }

        let active = true;
        const tm = setTimeout(async () => {
            setLoading(true);
            const params = {
                filter: {
                    // query,
                    filters: '[{"field":"category_code","op":"eq","value":"departure"}]',
                },
                urlSuffix: null,
                pagination: {
                    page: 1,
                    perPage: 300,
                },
            };
            const res = await dataAPI(GET_LIST, 'tour_tag', params);
            if (active) {
                let sorted_options = [...res.data];
                sorted_options.forEach((option) => {
                    if (isUSLocale()) {
                        option.name = convertToAmPm(option.name);
                    }
                });
                // filter by query
                if (query) {
                    sorted_options = sorted_options.filter((option) => option.name.toLowerCase().includes(query.toLowerCase()));
                }
                setOptions(sorted_options);
                if (!firstRender) {
                    setShowOptions(true);
                }
            }
            setLoading(false);
        }, 500);

        return () => {
            active = false;
            setFirstRender(false);
            clearTimeout(tm);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, newDepartureTag]);

    const handleTextOnBlur = (ev) => {
        setTimeout(() => {
            setShowOptions(false);
        }, 300);
    };

    const onOptionSelect = (option) => {
        onAddDeparture && onAddDeparture(option);
    };

    const handleCreateNewDeparture = (newTime) => {
        if (!newTime) {
            setPickerAnchorEl(null);
        }

        let new_time = newTime.format('HH:mm');
        setNewDepartureTag((prev) => ({ ...prev, name: new_time }));
        handleDepartureSave(new_time);
    };

    const handleDepartureSave = async (new_time) => {
        try {
            const res = await dataAPI(CREATE, 'tour_tag', { data: { ...newDepartureTag, name: new_time } });
            if (res.statusCode === 201) {
                enqueueSnackbar('Departure tag created', { variant: 'success' });
            } else if (res.statusCode === 406) {
                enqueueSnackbar('Departure tag is already existing', { variant: 'warning' });
            }
        } catch {
            enqueueSnackbar('Departure tag creation failed', { variant: 'error' });
        } finally {
            setNewDepartureTag((prev) => ({ ...prev, name: '' }));
        }
    };

    const hidePicker = (ev) => {
        setPickerAnchorEl(null);
        setNewDepartureTag((prev) => ({ ...prev, name: '' }));
    };


    const formatTimeValue = (value) => {
        if (isUSLocale()) {
            return convertToAmPm(value)
        }
        return value;
    }

    return (
        <Box className={classes.styledBox}>
            <Grid container direction="row" spacing={2} style={{ maxWidth: '600px', marginBottom: '1.5rem' }}>
                <Grid item xs={12} container direction="row" spacing={2}>
                    <Grid item xs={12}>
                        <Typography style={{ fontWeight: 'bold' }}>Trip Departures</Typography>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    {sortedDepartures.map((departure, index) => (
                        <Chip
                            key={index}
                            label={formatTimeValue(departure.tag.name)}
                            onDelete={() => disabled ? console.log("Tour write permission not found") : onDeleteDeparture('ev', index)}
                            style={{ margin: '5px' }} />
                    ))}
                </Grid>
                <Grid item xs={12}>
                    <>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Popper open={Boolean(pickerAnchorEl)} style={{ zIndex: 1 }} anchorEl={pickerAnchorEl} onClose={hidePicker}>
                                <ClickAwayListener
                                    onClickAway={() => {
                                        hidePicker();
                                    }}
                                >
                                    <Paper style={{ position: 'fixed', top: '10rem', left: isSmallScreen ? '3rem' : '40%', border: 'solid 1px #8d8d8d' }}>
                                        <StaticTimePicker onClose={() => hidePicker()} value={newDepartureTag.name ? moment(newDepartureTag.name) : null} onAccept={handleCreateNewDeparture} />
                                    </Paper>
                                </ClickAwayListener>
                            </Popper>
                        </LocalizationProvider>
                        <div style={{ zIndex: 1000, display: 'flex', alignItems: 'center', position: 'relative' }}>
                            <TextField
                                color="primary"
                                label="Add new departure"
                                size="small"
                                variant="filled"
                                disabled={disabled}
                                fullWidth
                                value={query || ''}
                                onClick={(ev) => setShowOptions(true)}
                                onBlur={handleTextOnBlur}
                                onChange={(ev) => setQuery(ev.target.value)}
                                InputProps={{
                                    endAdornment: <>{loading ? <CircularProgress color="primary" size={20} /> : null}</>,
                                }}
                                error={clientErrors.isMissingDeparture} 
                                helperText={clientErrors.isMissingDeparture ? 'Select at least one departure' : ''}
                            />
                            {showOptions && (
                                <div className={classes.optionsMenu}>
                                    <div className={classes.optionsWrapper}>
                                        {options.length > 0 &&
                                            options.map((option) => {
                                                return (
                                                    <div className={classes.optionItem} onClick={(ev) => onOptionSelect(option)}>
                                                        <Typography>{option.name}</Typography>
                                                    </div>
                                                );
                                            })}

                                        {options && options.length < 1 && (
                                            <div style={{ padding: '1rem' }}>
                                                <Typography>No matches found.</Typography>
                                            </div>
                                        )}
                                    </div>

                                    <div className={classes.addDepartureButton} onClick={(ev) => setPickerAnchorEl(true)}>
                                        <Button variant="outlined" color="primary"><AddIcon fontSize="small"/>&nbsp;Add departure</Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TripDepartures;
